// The page link field and the "Search and sharing" fields (title, description,
// social image, favicon and the link preview). The Page tab and the Share panel
// both show them, on the same page state.
import { useId, useState, type Ref } from 'react';
import { DEFAULT_FAVICON, type PageTheme } from '../model/pages';
import ImageInput from './ImageInput';
import { Field, TextAreaField, TextField } from './InspectorKit';

export type PageSeo = NonNullable<PageTheme['seo']>;
/** The page link's check result, shown under the link ("Available"). */
export type SlugMessage = { text: string; tone: string } | null;

/** The page link: "/p/" and the slug, with the availability line under it. */
export function PageLinkField({ slug, onChange, msg, bad = false, inputRef, label = 'Page link', hint }: {
  slug: string;
  onChange: (slug: string) => void;
  msg: SlugMessage;
  /** Marks the field invalid (a bad or taken link). */
  bad?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  label?: string;
  hint?: string;
}) {
  const id = useId();
  const describedBy = [msg ? `${id}-msg` : '', hint ? `${id}-hint` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <Field label={label} htmlFor={id}>
      <div className={`flex h-8 min-w-0 items-center rounded-lg border bg-surface transition focus-within:ring-[3px] [@media(pointer:coarse)]:h-10 ${bad
        ? 'border-danger focus-within:ring-danger/[.18]'
        : 'border-border hover:border-borderHi focus-within:border-primary focus-within:ring-primary/[.18]'}`}>
        <span aria-hidden className="shrink-0 select-none pl-2.5 text-sm text-textDim">/p/</span>
        <input id={id} ref={inputRef} aria-label={label} value={slug} spellCheck={false} autoComplete="off"
          aria-invalid={bad} aria-describedby={describedBy}
          className="h-full min-w-0 flex-1 bg-transparent pl-0.5 pr-2.5 text-sm text-text outline-none placeholder:text-textDim [@media(pointer:coarse)]:text-[16px]"
          onChange={(e) => onChange(e.target.value)} />
      </div>
      {msg && <span id={`${id}-msg`} role="status" className={`text-xs ${msg.tone}`}>{msg.text}</span>}
      {hint && !msg && <span id={`${id}-hint`} className="text-xs text-textDim">{hint}</span>}
    </Field>
  );
}

/** Title, description, social image, favicon, and how a shared link looks. */
export function SeoFields({ seo, fallback, setSeo, host, hosts = [], onHostChange }: {
  /** The owner's values. */
  seo: PageSeo;
  /** What the page uses with blank fields (resolvePageSeo). */
  fallback: { title: string; description: string; image: string; favicon: string };
  setSeo: (p: Partial<PageSeo>) => void;
  /** The host the preview shows. */
  host: string;
  /** Every host the page can be shared on. Two or more: a picker. */
  hosts?: string[];
  onHostChange?: (host: string) => void;
}) {
  const previewId = useId();
  const icon = fallback.favicon || DEFAULT_FAVICON;
  const [iconFailed, setIconFailed] = useState('');
  return (
    <>
      <TextField label="Page title" maxLength={70} placeholder={fallback.title} value={seo.title ?? ''}
        onChange={(v) => setSeo({ title: v })} hint={`${(seo.title ?? '').length}/70. Best under 60.`} />
      <TextAreaField label="Description" rows={2} maxLength={200}
        placeholder={fallback.description || 'A short summary that appears under the title.'}
        value={seo.description ?? ''} onChange={(v) => setSeo({ description: v })}
        hint={`${(seo.description ?? '').length}/200. Best around 150.`} />
      <ImageInput label="Social image" value={seo.image ?? ''} onChange={(url) => setSeo({ image: url })}
        placeholder="Image URL (1200×630 recommended)" />
      <ImageInput label="Favicon" value={seo.favicon ?? ''} onChange={(url) => setSeo({ favicon: url })}
        placeholder="Square PNG or SVG, 512×512" />
      <Field label="Link preview" labelId={previewId}
        aside={hosts.length > 1 && onHostChange ? (
          <select className="h-6 min-w-0 max-w-[60%] truncate rounded bg-transparent text-xs text-textDim hover:text-text" aria-label="Domain shown in the preview"
            value={host} onChange={(e) => onHostChange(e.target.value)}>
            {hosts.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        ) : undefined}>
        <div role="group" aria-labelledby={previewId} className="min-w-0 overflow-hidden rounded-lg border border-border" data-link-preview>
          {fallback.image
            ? <img src={fallback.image} alt="" className="aspect-[1200/630] w-full bg-surface2 object-cover" />
            : <div className="grid aspect-[1200/630] w-full min-w-0 place-items-center bg-surface2 text-xs text-textDim">No image yet</div>}
          <div className="grid min-w-0 gap-0.5 border-t border-border bg-surface px-3 py-2">
            <span className="flex min-w-0 items-center gap-1.5 text-xs text-textDim">
              {iconFailed !== icon
                ? <img src={icon} alt="" width={14} height={14} onError={() => setIconFailed(icon)} className="h-3.5 w-3.5 shrink-0 rounded-[3px] object-contain" data-preview-favicon />
                : <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-[3px] bg-surface2" />}
              <span className="min-w-0 truncate">{host}</span>
            </span>
            <span className="truncate text-sm font-semibold text-text">{fallback.title}</span>
            <span className="line-clamp-2 text-xs text-textDim">{fallback.description || 'Add a description to control the preview text.'}</span>
          </div>
        </div>
      </Field>
    </>
  );
}
